/**
 * @file CustomerProfile.js
 * @description Page component for managing customer profiles, document capture/scanning, cheque history, and identity verification.
 */

import "../css/pages/CustomerProfile.css";
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { imgUrl } from "../utils/imgUrl";
import DocumentCameraModal from "../components/DocumentCameraModal";
import DateInput from "../components/DateInput";
import {
  hasReadableScanData,
  prepareDocumentImage,
  fileToBase64,
} from "../utils/documentImage";
import MasterDropdown from "../components/MasterDropdown";
import {
  getCustomer,
  getCustomerCheques,
  updateCustomer,
  addIdentification,
  addContact,
  cashCheque,
  uploadCustomerProfile,
  uploadCustomerIdFront,
  uploadChequeFront,
  getBanks,
  getCompanies,
  scanDriversLicense,
  scanChequeImage,
  cropDocumentImage,
  recropStoredChequeImage,
} from "../api/services";
import { formatDate, capitalizeName, getTodayChicago } from "../utils/formatDate";
import toast from "react-hot-toast";

/**
 * Formats a numeric value as US currency string.
 * @param {number|string} n - Dollar amount.
 * @returns {string} Formatted currency.
 */
const fmt = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 });

/**
 * Formats a date value into human readable date format.
 * @param {string|Date} d - Date value.
 * @returns {string} Formatted date.
 */
const fmtD = (d) => formatDate(d);

/**
 * Checks if any of the customer's identifications have expired.
 * @param {Object} customer - Customer object.
 * @returns {boolean} True if expired ID present.
 */
const isIdExpired = (customer) =>
  (customer?.identifications || []).some(
    (idDoc) => idDoc.expiryDate && new Date(idDoc.expiryDate) < new Date(),
  );

const STATUS_COLOR = {
  Cashed: "#22c55e",
  Pending: "#f59e0b",
  Returned: "#ef4444",
  Void: "#9ca3af",
  Hold: "#3b82f6",
};

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

/* ── tiny sub-components ─────────────────────────────────── */
const ActionBtn = ({ label, color = "#3a8a5a", onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      width: "100%",
      padding: "7px 0",
      marginBottom: 4,
      background: color,
      color: "white",
      border: "none",
      borderRadius: 4,
      fontWeight: 700,
      fontSize: 13,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      transition: "filter 0.15s",
    }}
    onMouseEnter={(e) =>
      !disabled && (e.currentTarget.style.filter = "brightness(1.1)")
    }
    onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
  >
    {label}
  </button>
);

const TabBtn = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: "4px 14px",
      fontSize: 12.5,
      fontWeight: active ? 700 : 400,
      background: active ? "#d4edda" : "#e9ecef",
      border: "1px solid #adb5bd",
      borderBottom: active ? "1px solid #d4edda" : "1px solid #adb5bd",
      borderRadius: "4px 4px 0 0",
      cursor: "pointer",
      color: active ? "#155724" : "#495057",
      marginRight: 2,
      marginBottom: -1,
      position: "relative",
      zIndex: active ? 2 : 1,
    }}
  >
    {label}
  </button>
);

const LblInput = ({
  label,
  value,
  onChange,
  readOnly,
  style = {},
  type = "text",
  children,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginBottom: 5,
      ...style,
    }}
  >
    <span
      style={{
        fontSize: 12,
        color: "#555",
        minWidth: 90,
        textAlign: "right",
        flexShrink: 0,
      }}
    >
      {label}:
    </span>
    {children || (
      <input
        type={type}
        readOnly={readOnly}
        value={value || ""}
        onChange={onChange}
        style={{
          flex: 1,
          padding: "2px 6px",
          fontSize: 12.5,
          border: "1px solid #adb5bd",
          borderRadius: 3,
          background: readOnly ? "#f8f9fa" : "white",
          outline: "none",
          minWidth: 0,
        }}
      />
    )}
  </div>
);

/**
 * Quick cash check modal dialog for cashing a check directly from customer profile.
 * @component
 * @param {Object} props
 * @param {Object} props.customer - Customer details object.
 * @param {Function} props.onClose - Modal close handler.
 * @param {Function} props.onSuccess - Success callback handler.
 */
function CashCheckModal({ customer, onClose, onSuccess }) {
  const [form, setForm] = useState({
    checkNumber: "",
    amount: "",
    fee: "",
    maker: "",
    licenseNumber: customer?.identifications?.[0]?.idNumber || "",
    checkDate: "",
    cashedOn: getTodayChicago(),
    status: "Cashed",
    checkType: "Personal",
    isLoan: false,
    notes: "",
    bank: { name: "", routingNumber: "" },
  });
  const [saving, setSaving] = useState(false);
  const [checkFrontFile, setCheckFrontFile] = useState(null);
  const [checkFrontPreview, setCheckFrontPreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const frontRef = useRef();

  // Load banks and companies for dropdowns
  useEffect(() => {
    getBanks({ limit: 9999 })
      .then((r) => setBankList(r.data.data || []))
      .catch(() => {});
    getCompanies({ isBlocked: "false", limit: 9999 })
      .then((r) => setCompanyList(r.data.data || []))
      .catch(() => {});
  }, []);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const payOut =
    form.amount && form.fee !== ""
      ? (parseFloat(form.amount || 0) - parseFloat(form.fee || 0)).toFixed(2)
      : "";

  const handleImageFile = async (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setCheckFrontFile(file);
    setCheckFrontPreview(url);
    // Auto-scan the check image
    setScanning(true);
    const scanToast = toast.loading("🔍 Scanning check image...");
    try {
      const prepared = await prepareDocumentImage(file, {
        rejectBlur: false,
        fileName: `check-front-${Date.now()}.jpg`,
      });
      const res = await scanChequeImage(prepared.base64, prepared.mimeType);
      const d = res.data?.data;
      if (d) {
        if (d.maker && !form.maker) s("maker", d.maker);
        if (d.checkNumber && !form.checkNumber) s("checkNumber", d.checkNumber);
        if (d.date && !form.checkDate) s("checkDate", d.date);
        if (d.amount && !form.amount) s("amount", d.amount);
        if (d.licenseNumber && !form.licenseNumber)
          s("licenseNumber", d.licenseNumber);
        if (d.bankName)
          s("bank", { name: d.bankName, routingNumber: d.routingNumber || "" });
        toast.success("✅ Check scanned! Fields auto-filled.", {
          id: scanToast,
        });
      } else {
        toast.dismiss(scanToast);
      }
    } catch {
      toast.dismiss(scanToast);
    } finally {
      setScanning(false);
    }
  };

  const submit = async () => {
    if (!form.checkNumber || !form.amount || form.fee === "") {
      toast.error("Check #, Amount and Fee are required");
      return;
    }
    if (!form.maker || !form.maker.trim()) {
      toast.error("Company Name (Maker) is required");
      return;
    }
    setSaving(true);
    try {
      const res = await cashCheque({
        ...form,
        customer: customer._id,
        amount: parseFloat(form.amount),
        fee: parseFloat(form.fee),
      });
      const chequeId = res.data.data?._id;
      // Upload check image if provided
      if (chequeId && checkFrontFile) {
        try {
          await uploadChequeFront(chequeId, checkFrontFile);
        } catch (uploadErr) {
          console.error("Image upload failed:", uploadErr);
          toast.error("Check cashed but image upload failed.");
        }
      }
      toast.success("Check cashed!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 8,
          width: 640,
          boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
          fontFamily: "sans-serif",
        }}
      >
        {/* title bar */}
        <div
          style={{
            background: "#1a7a4a",
            color: "white",
            padding: "8px 14px",
            borderRadius: "8px 8px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 14 }}>
            💵 Cash Check — {capitalizeName(customer.firstName)}{" "}
            {capitalizeName(customer.lastName)}
          </span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: 18,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {showCamera && (
          <DocumentCameraModal
            open={showCamera}
            title="Scan Check Front"
            label="check front"
            aspectRatio={2.15}
            onClose={() => setShowCamera(false)}
            onCapture={(file) => {
              setShowCamera(false);
              handleImageFile(file);
            }}
          />
        )}
        <div style={{ padding: 18 }}>
          {/* Check Image Upload */}
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              background: "#f0fdf4",
              border: "1.5px solid #86efac",
              borderRadius: 8,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#166534",
                marginBottom: 7,
              }}
            >
              📷 Check Image{" "}
              {scanning && (
                <span style={{ fontWeight: 400, color: "#6b7280" }}>
                  — scanning...
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                ref={frontRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  handleImageFile(e.target.files[0]);
                  e.target.value = "";
                }}
              />
              {checkFrontPreview ? (
                <div style={{ position: "relative" }}>
                  <img
                    src={checkFrontPreview}
                    alt="Check"
                    style={{
                      height: 52,
                      borderRadius: 5,
                      border: "1px solid #86efac",
                    }}
                  />
                  <button
                    onClick={() => {
                      setCheckFrontFile(null);
                      setCheckFrontPreview(null);
                    }}
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      background: "#dc2626",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                      fontSize: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    fontStyle: "italic",
                  }}
                >
                  No image yet
                </div>
              )}
              <button
                className="btn btn-outline btn-sm"
                onClick={() => frontRef.current?.click()}
              >
                📁 Gallery
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowCamera(true)}
              >
                📷 Camera
              </button>
            </div>
          </div>

          {/* Row 1: Check Number | Date on Check | Cashed On */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 3,
                }}
              >
                Check Number *
              </div>
              <input
                type="text"
                value={form.checkNumber}
                onChange={(e) => s("checkNumber", e.target.value)}
                style={{
                  width: "100%",
                  padding: "5px 8px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  fontSize: 13,
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 3,
                }}
              >
                Date on Check *
              </div>
              <DateInput
                value={form.checkDate}
                onChange={(e) => s("checkDate", e.target.value)}
                style={{
                  width: "100%",
                  padding: "5px 8px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 3,
                }}
              >
                Cashed On *{" "}
                <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                  (Transaction Date)
                </span>
              </div>
              <DateInput
                value={form.cashedOn}
                onChange={(e) => s("cashedOn", e.target.value)}
                style={{
                  width: "100%",
                  padding: "5px 8px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              />
            </div>
          </div>

          {/* Row 2: Maker / Payer Name full width */}
          <div style={{ marginBottom: 10 }}>
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: "#b91c1c",
                marginBottom: 3,
              }}
            >
              Maker / Payer Name *{" "}
              <span style={{ fontWeight: 400, color: "#6b7280" }}>
                (FROM COMPANY MASTER)
              </span>
            </div>
            <MasterDropdown
              value={form.maker}
              onChange={(val) => s("maker", val)}
              items={companyList}
              placeholder="Select or type company name…"
              icon="🏢"
              renderBadge={(item) =>
                item.isBlocked ? (
                  <span
                    style={{
                      fontSize: 10,
                      background: "#fef2f2",
                      color: "#b91c1c",
                      padding: "2px 7px",
                      borderRadius: 10,
                      fontWeight: 700,
                    }}
                  >
                    BLOCKED
                  </span>
                ) : null
              }
            />
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
              The business/person who wrote the check
            </div>
          </div>

          {/* Amounts highlighted */}
          <div
            style={{
              background: "#f0fdf4",
              border: "1.5px solid #86efac",
              borderRadius: 8,
              padding: 12,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            {[
              ["Amount ($) *", "amount"],
              ["Fee ($) *", "fee"],
            ].map(([l, k]) => (
              <div key={k}>
                <div
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "#555",
                    marginBottom: 3,
                  }}
                >
                  {l}
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form[k]}
                  onChange={(e) => s(k, e.target.value)}
                  style={{
                    width: "100%",
                    padding: "5px 8px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    fontSize: 16,
                    fontWeight: 800,
                  }}
                />
              </div>
            ))}
            <div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 3,
                }}
              >
                Pay Out
              </div>
              <div
                style={{
                  padding: "5px 8px",
                  background: "#dcfce7",
                  border: "1px solid #86efac",
                  borderRadius: 4,
                  fontSize: 18,
                  fontWeight: 900,
                  color: "#16a34a",
                }}
              >
                {payOut ? fmt(payOut) : "—"}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 3,
                }}
              >
                Bank Name
              </div>
              <MasterDropdown
                value={form.bank.name}
                onChange={(val) =>
                  setForm((f) => ({
                    ...f,
                    bank: { ...f.bank, name: val },
                  }))
                }
                items={bankList}
                placeholder="Search bank name…"
                icon="🏦"
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 3,
                }}
              >
                Status
              </div>
              <select
                value={form.status}
                onChange={(e) => s("status", e.target.value)}
                style={{
                  width: "100%",
                  padding: "5px 8px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              >
                {["Cashed", "Pending", "Hold", "Returned", "Void"].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              display: "none",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <input
              type="checkbox"
              id="loanChk"
              checked={form.isLoan}
              onChange={(e) => s("isLoan", e.target.checked)}
            />
            <label
              htmlFor="loanChk"
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#dc2626",
                cursor: "pointer",
              }}
            >
              🔴 This is a LOAN / Payday Advance
            </label>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: "7px 20px",
                border: "1px solid #ccc",
                background: "white",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving}
              style={{
                padding: "7px 24px",
                background: "#1a7a4a",
                color: "white",
                border: "none",
                borderRadius: 4,
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              {saving ? "Processing..." : "💵 Cash Check"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Main CustomerProfile page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("customer");
  const [subTab, setSubTab] = useState("cashing"); // "cashing" | "display"
  const [chequeModal, setChequeModal] = useState(null); // { ch, side: 'front'|'back' }
  const [recroppingKey, setRecroppingKey] = useState(null); // `${chequeId}-${side}` currently re-cropping
  const [imgCacheBust, setImgCacheBust] = useState({}); // `${chequeId}-${side}` -> timestamp, forces <img> reload after re-crop
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [showCash, setShowCash] = useState(false);
  const [showAddId, setShowAddId] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newId, setNewId] = useState({
    type: "Driver License",
    idNumber: "",
    issuedState: "AL",
    issuedClass: "D",
    expiryDate: "",
  });
  const [newContact, setNewContact] = useState({
    type: "Home Phone",
    value: "",
  });
  const photoRef = useRef();
  const idFrontRef = useRef();
  const [scanningId, setScanningId] = useState(false);
  const [idScanned, setIdScanned] = useState(false);
  const [showProfileCamera, setShowProfileCamera] = useState(false);
  const [idLightbox, setIdLightbox] = useState(null); // url string to show enlarged
  const idClickTimer = useRef(null);

  const load = () => {
    Promise.all([getCustomer(id), getCustomerCheques(id, { limit: 300 })])
      .then(([cr, chr]) => {
        setCustomer(cr.data.data);
        setForm(cr.data.data);
        setCheques(chr.data.data);
      })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const save = async () => {
    try {
      const r = await updateCustomer(id, form);
      setCustomer(r.data.data);
      setEditMode(false);
      toast.success("Saved!");
    } catch {
      toast.error("Save failed");
    }
  };

  const handleAddId = async () => {
    try {
      const r = await addIdentification(id, newId);
      setCustomer(r.data.data);
      setShowAddId(false);
      setNewId({
        type: "Driver License",
        idNumber: "",
        issuedState: "AL",
        issuedClass: "D",
        expiryDate: "",
      });
      toast.success("ID added");
    } catch {
      toast.error("Failed");
    }
  };

  const handleAddContact = async () => {
    try {
      const r = await addContact(id, newContact);
      setCustomer(r.data.data);
      setShowAddContact(false);
      setNewContact({ type: "Home Phone", value: "" });
      toast.success("Contact added");
    } catch {
      toast.error("Failed");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      toast.loading("Uploading...", { id: "photo-upload" });
      const r = await uploadCustomerProfile(id, file);
      setCustomer(r.data.data);
      // Keep `form` in sync too — otherwise clicking the main Save button
      // later would send the OLD photo value and overwrite this new upload
      // back to the stale filename in the database.
      setForm((f) => ({ ...f, photo: r.data.data.photo }));
      toast.success("Profile photo updated!", { id: "photo-upload" });
    } catch {
      toast.error("Photo upload failed", { id: "photo-upload" });
    }
  };

  const handleProfilePhotoFromCamera = async (file) => {
    setShowProfileCamera(false);
    if (!file) return;
    try {
      toast.loading("Uploading...", { id: "photo-upload" });
      const r = await uploadCustomerProfile(id, file);
      setCustomer(r.data.data);
      setForm((f) => ({ ...f, photo: r.data.data.photo }));
      toast.success("Profile photo updated!", { id: "photo-upload" });
    } catch {
      toast.error("Photo upload failed", { id: "photo-upload" });
    }
  };

  // Re-crop an already-stored cheque image (e.g. one uploaded before cropping
  // existed, or that came out uncropped). Calls the backend, which reads the
  // file off disk, sends it to the Python crop API, and overwrites the same
  // file in place. We then bump a cache-busting timestamp so the <img> tag
  // actually reloads the new bytes instead of showing the cached old image.
  const handleRecropCheque = async (chequeId, side) => {
    const key = `${chequeId}-${side}`;
    setRecroppingKey(key);
    const toastId = toast.loading(`Cropping ${side} image...`);
    try {
      const res = await recropStoredChequeImage(chequeId, side);
      if (res.data?.success) {
        setImgCacheBust((prev) => ({ ...prev, [key]: Date.now() }));
        toast.success(
          res.data.detected
            ? "✅ Image re-cropped!"
            : "No document border detected — image left as-is.",
          { id: toastId },
        );
      } else {
        toast.error(res.data?.message || "Crop failed", { id: toastId });
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Crop failed. Please try again.",
        { id: toastId },
      );
    } finally {
      setRecroppingKey(null);
    }
  };

  const handleIdFrontUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanningId(true);
    setIdScanned(false);
    const toastId = toast.loading("🔍 Scanning ID — reading fields...");
    try {
      // Step 1 — CROP: send the raw ID photo to the Python crop API (same
      // service used for checks) so the stored image is a clean, straight
      // crop of just the card — not the desk/hand/background around it.
      let croppedFile = file;
      try {
        const rawBase64 = await fileToBase64(file);
        const cropRes = await cropDocumentImage(rawBase64, file.type || "image/jpeg");
        const cd = cropRes.data;
        if (cd?.success && cd?.image) {
          const mimeType = cd.mimeType || "image/jpeg";
          const blob = await fetch(`data:${mimeType};base64,${cd.image}`).then(
            (r) => r.blob(),
          );
          const ext = mimeType.includes("png") ? "png" : "jpg";
          croppedFile = new File(
            [blob],
            `customer-id-front-${Date.now()}-cropped.${ext}`,
            { type: mimeType, lastModified: Date.now() },
          );
        }
      } catch (cropErr) {
        console.warn("ID crop failed, using original image:", cropErr.message);
      }

      // Step 2 — upload the (cropped) image to the server
      const uploadRes = await uploadCustomerIdFront(id, croppedFile);
      setCustomer(uploadRes.data.data);
      // Same fix as above — sync `form.photo` immediately so that clicking
      // the main Save button afterward (which PATCHes the whole `form`)
      // can't overwrite this new idFront filename with the old stale one.
      setForm((f) => ({ ...f, photo: uploadRes.data.data.photo }));

      // Step 3 — CHECK: run OCR on the same cropped image
      const base64 = await fileToBase64(croppedFile);
      const mimeType = croppedFile.type || "image/jpeg";
      const scanRes = await scanDriversLicense(base64, mimeType);
      let d = scanRes.data?.data;

      if (hasReadableScanData(d)) {
        const updates = {};
        if (d.firstName) updates.firstName = d.firstName;
        if (d.lastName) updates.lastName = d.lastName;
        if (d.middleName) updates.middleName = d.middleName;
        if (d.dateOfBirth) updates.dateOfBirth = d.dateOfBirth;
        if (d.sex) updates.sex = d.sex;
        if (d.height) updates.height = d.height;
        if (d.eyeColor) updates.eyeColor = d.eyeColor;
        if (d.address) {
          updates.address = {
            ...(form.address || {}),
            street: d.address?.street || form.address?.street || "",
            city: d.address?.city || form.address?.city || "",
            state: d.address?.state || form.address?.state || "",
            zip: d.address?.zip || form.address?.zip || "",
            country: form.address?.country || "USA",
          };
        }
        if (d.idNumber || d.issuedState || d.expiryDate) {
          const existingIds = [...(form.identifications || [])];
          if (existingIds.length > 0) {
            existingIds[0] = {
              ...existingIds[0],
              idNumber: d.idNumber || existingIds[0].idNumber,
              issuedState: d.issuedState || existingIds[0].issuedState,
              expiryDate: d.expiryDate || existingIds[0].expiryDate,
            };
          }
          updates.identifications = existingIds;
        }
        setForm((f) => ({ ...f, ...updates }));
        setEditMode(true);
        toast.success("✅ ID scanned! Review the auto-filled fields.", {
          id: toastId,
        });
      } else {
        toast.success("ID image uploaded!", { id: toastId });
      }
      setIdScanned(true);
    } catch (err) {
      toast.error("ID scan failed", { id: toastId });
    } finally {
      setScanningId(false);
      e.target.value = "";
    }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) =>
    setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  if (loading)
    return (
      <Layout title="Customer Profile">
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <div className="spinner spinner-dark" />
        </div>
      </Layout>
    );
  if (!customer)
    return (
      <Layout title="Customer Profile">
        <div style={{ padding: 40, textAlign: "center" }}>
          Customer not found
        </div>
      </Layout>
    );

  const c = editMode ? form : customer;

  const totalCashed = cheques.reduce((s, x) => s + (x.amount || 0), 0);
  const yearlyTotal = customer.yearlyActivityTotal || totalCashed;

  /* ── RENDER ─────────────────────────────────────────────── */
  return (
    <Layout
      title={`Profile — ${capitalizeName(customer.firstName)} ${capitalizeName(customer.lastName)}`}
    >
      {/* Back bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <button
          onClick={() => navigate("/customers")}
          style={{
            fontSize: 12,
            padding: "3px 10px",
            cursor: "pointer",
            border: "1px solid #ccc",
            background: "#f8f9fa",
            borderRadius: 3,
          }}
        >
          ← Back to Customers
        </button>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#1a7a4a" }}>
          Profile — {capitalizeName(customer.firstName)}{" "}
          {customer.middleName || ""} {capitalizeName(customer.lastName)}
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontSize: 13,
            fontWeight: 800,
            color: customer.yearlyActivityTotal > 0 ? "#dc2626" : "#1a7a4a",
          }}
        >
          Yearly Activity Total: {fmt(yearlyTotal)}
        </span>
      </div>

      {/* ── MAIN 3-COLUMN LAYOUT ── */}
      <div
        className="profile-3col"
        style={{
          display: "flex",
          gap: 0,
          background: "#e9ecef",
          border: "2px solid #adb5bd",
          borderRadius: 6,
          overflow: "hidden",
          fontFamily: "Tahoma, Arial, sans-serif",
        }}
      >
        {/* ── LEFT: Photos + Fingerprint ── */}
        <div
          className="profile-left-col"
          style={{
            width: 200,
            background: "#f8f9fa",
            borderRight: "1px solid #adb5bd",
            padding: 8,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {/* Profile photo */}
          <div style={{ position: "relative" }}>
            <div
              style={{
                width: "100%",
                height: 140,
                background: "#dee2e6",
                border: "1px solid #adb5bd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                borderRadius: 3,
              }}
            >
              {customer.photo?.profile ? (
                <img
                  src={imgUrl(customer.photo.profile)}
                  alt="Profile"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{ textAlign: "center", color: "#6c757d" }}>
                  <div style={{ fontSize: 40 }}>?</div>
                  <div style={{ fontSize: 11 }}>NO IMAGE</div>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={photoRef}
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: "none" }}
            />
            <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
              <button
                onClick={() => photoRef.current.click()}
                title="Choose from gallery"
                style={{
                  flex: 1,
                  fontSize: 11,
                  padding: "5px 4px",
                  border: "1px solid #adb5bd",
                  background: "#e9ecef",
                  cursor: "pointer",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  fontWeight: 600,
                  color: "#495057",
                }}
              >
                🖼️ Gallery
              </button>
              <button
                onClick={() => setShowProfileCamera(true)}
                title="Take photo with camera"
                style={{
                  flex: 1,
                  fontSize: 11,
                  padding: "5px 4px",
                  border: "1px solid var(--primary)",
                  background: "var(--primary-light)",
                  cursor: "pointer",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  fontWeight: 600,
                  color: "var(--primary)",
                }}
              >
                📷 Camera
              </button>
            </div>
          </div>

          {/* ID 1 */}
          <div>
            <div style={{ display: "flex", gap: 4, marginBottom: 3 }}>
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  background: "#0d6efd",
                  color: "white",
                  padding: "1px 6px",
                  borderRadius: 2,
                }}
              >
                ID 1
              </span>
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  background: "#6c757d",
                  color: "white",
                  padding: "1px 6px",
                  borderRadius: 2,
                }}
              >
                ID 2
              </span>
            </div>

            {/* Scan status banner */}
            {(scanningId || idScanned) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 8px",
                  borderRadius: 4,
                  marginBottom: 4,
                  background: scanningId ? "#f0fdf4" : "#ecfdf5",
                  border: `1.5px dashed ${scanningId ? "#16a34a" : "#10b981"}`,
                }}
              >
                {scanningId ? (
                  <>
                    <div
                      style={{
                        width: 13,
                        height: 13,
                        border: "2px solid #16a34a",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#15803d",
                      }}
                    >
                      🤖 Reading ID...
                    </span>
                  </>
                ) : (
                  <span
                    style={{ fontSize: 11, fontWeight: 700, color: "#059669" }}
                  >
                    ✅ ID scanned!
                  </span>
                )}
              </div>
            )}

            <input
              type="file"
              ref={idFrontRef}
              accept="image/*"
              onChange={handleIdFrontUpload}
              style={{ display: "none" }}
            />
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (scanningId) return;
                // Distinguish single vs double click
                if (idClickTimer.current) {
                  // Second click within 280ms → double-click → open lightbox
                  clearTimeout(idClickTimer.current);
                  idClickTimer.current = null;
                  if (customer.photo?.idFront)
                    setIdLightbox(imgUrl(customer.photo.idFront));
                } else {
                  // First click — wait to see if second follows
                  idClickTimer.current = setTimeout(() => {
                    idClickTimer.current = null;
                    // Single click → open file picker (only if no photo yet or want to replace)
                    idFrontRef.current.click();
                  }, 280);
                }
              }}
              title={
                customer.photo?.idFront
                  ? "Click to replace · Double-click to enlarge"
                  : "Click to upload & scan ID"
              }
              style={{
                width: "100%",
                height: 90,
                background: scanningId ? "#f0fdf4" : "#dee2e6",
                border: scanningId
                  ? "1.5px solid #16a34a"
                  : "1px dashed #adb5bd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 3,
                overflow: "hidden",
                cursor: scanningId ? "default" : "pointer",
                transition: "all 0.3s",
                position: "relative",
              }}
            >
              {scanningId ? (
                <div style={{ textAlign: "center", color: "#16a34a" }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      border: "3px solid #16a34a",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                      margin: "0 auto 6px",
                    }}
                  />
                  <div style={{ fontSize: 10, fontWeight: 700 }}>
                    Scanning...
                  </div>
                </div>
              ) : customer.photo?.idFront ? (
                <>
                  <img
                    src={imgUrl(customer.photo.idFront)}
                    alt="ID"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {/* Double-click hint overlay */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 3,
                      right: 4,
                      background: "rgba(0,0,0,0.55)",
                      borderRadius: 3,
                      padding: "1px 5px",
                      fontSize: 9,
                      color: "white",
                      pointerEvents: "none",
                      letterSpacing: 0.2,
                    }}
                  >
                    🔍 dbl-click to zoom
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center", color: "#6c757d" }}>
                  <div style={{ fontSize: 22 }}>🪪</div>
                  <div style={{ fontSize: 10 }}>Click to scan ID</div>
                </div>
              )}
            </div>
          </div>

          {/* Fingerprint section — hidden */}
        </div>

        {/* ── CENTER: Tabs + Form + Table ── */}
        <div
          className="profile-center-col"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Tab bar */}
          <div
            style={{
              display: "flex",
              padding: "6px 8px 0",
              background: "#f0f0f0",
              borderBottom: "1px solid #adb5bd",
            }}
          >
            {[
              ["customer", "Customer"],
              ["details", "Details"],
              ["extended", "Extended Information"],
              ["misc", "Misc."],
            ].map(([k, l]) => (
              <TabBtn
                key={k}
                label={l}
                active={tab === k}
                onClick={() => setTab(k)}
              />
            ))}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: 6,
                alignItems: "center",
                paddingBottom: 4,
              }}
            >
              {editMode ? (
                <>
                  <button
                    onClick={save}
                    style={{
                      fontSize: 11.5,
                      padding: "3px 12px",
                      background: "#1a7a4a",
                      color: "white",
                      border: "none",
                      borderRadius: 3,
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    💾 Save
                  </button>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setForm(customer);
                    }}
                    style={{
                      fontSize: 11.5,
                      padding: "3px 10px",
                      border: "1px solid #adb5bd",
                      background: "white",
                      borderRadius: 3,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  style={{
                    fontSize: 11.5,
                    padding: "3px 12px",
                    border: "1px solid #adb5bd",
                    background: "#e9ecef",
                    borderRadius: 3,
                    cursor: "pointer",
                  }}
                >
                  ✏️ Edit
                </button>
              )}
            </div>
          </div>

          {/* Tab content */}
          <div
            style={{
              padding: "10px 12px",
              background: "white",
              borderBottom: "1px solid #dee2e6",
              minHeight: 200,
            }}
          >
            {/* CUSTOMER TAB */}
            {tab === "customer" && (
              <div style={{ display: "flex", gap: 20 }}>
                {/* Left: name + address */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 5 }}>
                    <div style={{ flex: 2 }}>
                      <div
                        style={{ fontSize: 11, color: "#555", marginBottom: 2 }}
                      >
                        First Name:
                      </div>
                      <input
                        readOnly={!editMode}
                        value={c.firstName || ""}
                        onChange={(e) => setF("firstName", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "2px 6px",
                          border: "1px solid #adb5bd",
                          borderRadius: 2,
                          fontSize: 12.5,
                          background: !editMode ? "#f8f9fa" : "white",
                          textTransform: "uppercase",
                        }}
                      />
                    </div>
                    <div style={{ width: 50 }}>
                      <div
                        style={{ fontSize: 11, color: "#555", marginBottom: 2 }}
                      >
                        Mid. Int.:
                      </div>
                      <input
                        readOnly={!editMode}
                        value={c.middleName || ""}
                        onChange={(e) => setF("middleName", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "2px 4px",
                          border: "1px solid #adb5bd",
                          borderRadius: 2,
                          fontSize: 12.5,
                          background: !editMode ? "#f8f9fa" : "white",
                          textTransform: "uppercase",
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: 5 }}>
                    <div
                      style={{ fontSize: 11, color: "#555", marginBottom: 2 }}
                    >
                      Last Name:
                    </div>
                    <input
                      readOnly={!editMode}
                      value={c.lastName || ""}
                      onChange={(e) => setF("lastName", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "2px 6px",
                        border: "1px solid #adb5bd",
                        borderRadius: 2,
                        fontSize: 12.5,
                        background: !editMode ? "#f8f9fa" : "white",
                        textTransform: "uppercase",
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: 5 }}>
                    <div
                      style={{ fontSize: 11, color: "#555", marginBottom: 2 }}
                    >
                      Address:
                    </div>
                    <input
                      readOnly={!editMode}
                      value={
                        [c.address?.street, c.address?.apt]
                          .filter(Boolean)
                          .join(" ") || ""
                      }
                      onChange={(e) => setAddr("street", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "2px 6px",
                        border: "1px solid #adb5bd",
                        borderRadius: 2,
                        fontSize: 12.5,
                        background: !editMode ? "#f8f9fa" : "white",
                        textTransform: "uppercase",
                      }}
                      placeholder="123 Main St. Apt #456"
                    />
                  </div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 5 }}>
                    <div style={{ flex: 2 }}>
                      <div
                        style={{ fontSize: 11, color: "#555", marginBottom: 2 }}
                      >
                        City:
                      </div>
                      <input
                        readOnly={!editMode}
                        value={c.address?.city || ""}
                        onChange={(e) => setAddr("city", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "2px 6px",
                          border: "1px solid #adb5bd",
                          borderRadius: 2,
                          fontSize: 12.5,
                          background: !editMode ? "#f8f9fa" : "white",
                          textTransform: "uppercase",
                        }}
                      />
                    </div>
                    <div style={{ width: 44 }}>
                      <div
                        style={{ fontSize: 11, color: "#555", marginBottom: 2 }}
                      >
                        State:
                      </div>
                      {editMode ? (
                        <select
                          value={c.address?.state || "AL"}
                          onChange={(e) => setAddr("state", e.target.value)}
                          style={{
                            width: "100%",
                            padding: "2px 2px",
                            border: "1px solid #adb5bd",
                            borderRadius: 2,
                            fontSize: 11.5,
                          }}
                        >
                          {US_STATES.map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          readOnly
                          value={c.address?.state || ""}
                          style={{
                            width: "100%",
                            padding: "2px 4px",
                            border: "1px solid #adb5bd",
                            borderRadius: 2,
                            fontSize: 12.5,
                            background: "#f8f9fa",
                          }}
                        />
                      )}
                    </div>
                    <div style={{ width: 64 }}>
                      <div
                        style={{ fontSize: 11, color: "#555", marginBottom: 2 }}
                      >
                        Zip:
                      </div>
                      <input
                        readOnly={!editMode}
                        value={c.address?.zip || ""}
                        onChange={(e) => setAddr("zip", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "2px 4px",
                          border: "1px solid #adb5bd",
                          borderRadius: 2,
                          fontSize: 12.5,
                          background: !editMode ? "#f8f9fa" : "white",
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div
                      style={{ fontSize: 11, color: "#555", marginBottom: 2 }}
                    >
                      Country:
                    </div>
                    <input
                      readOnly={!editMode}
                      value={c.address?.country || "USA"}
                      onChange={(e) => setAddr("country", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "2px 6px",
                        border: "1px solid #adb5bd",
                        borderRadius: 2,
                        fontSize: 12.5,
                        background: !editMode ? "#f8f9fa" : "white",
                        textTransform: "uppercase",
                      }}
                    />
                  </div>
                </div>

                {/* Right: Identifications */}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#495057",
                      marginBottom: 4,
                    }}
                  >
                    Identifications:
                  </div>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                      border: "1px solid #adb5bd",
                      marginBottom: 6,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#e9ecef" }}>
                        {["Type", "ID Number", "Re."].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "3px 6px",
                              border: "1px solid #adb5bd",
                              textAlign: "left",
                              fontWeight: 600,
                              fontSize: 11.5,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customer.identifications?.map((id, i) => (
                        <tr
                          key={i}
                          style={{
                            background: i % 2 === 0 ? "white" : "#f8f9fa",
                          }}
                        >
                          <td
                            style={{
                              padding: "3px 6px",
                              border: "1px solid #dee2e6",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: 4,
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontSize: 13 }}>
                                {id.type === "Driver License"
                                  ? "🪪"
                                  : id.type === "Social Security #"
                                    ? "🔒"
                                    : "📋"}
                              </span>
                              <span>{id.type}</span>
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "3px 6px",
                              border: "1px solid #dee2e6",
                              fontFamily: "monospace",
                              fontWeight: 700,
                            }}
                          >
                            {id.type === "Social Security #" ? (
                              <span
                                title={id.idNumber}
                                style={{ color: "#6c757d" }}
                              >
                                {"***-**-" +
                                  (id.idNumber || "")
                                    .slice(-4)
                                    .padStart(4, "*")}
                                <span
                                  style={{
                                    fontSize: 10,
                                    color: "#adb5bd",
                                    marginLeft: 6,
                                    fontFamily: "sans-serif",
                                    fontWeight: 400,
                                  }}
                                >
                                  🔒
                                </span>
                              </span>
                            ) : (
                              id.idNumber
                            )}
                          </td>
                          <td
                            style={{
                              padding: "3px 6px",
                              border: "1px solid #dee2e6",
                              fontSize: 11,
                            }}
                          >
                            {id.issuedState || ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                    <button
                      onClick={() => setShowAddId(true)}
                      style={{
                        fontSize: 11.5,
                        padding: "3px 10px",
                        border: "1px solid #adb5bd",
                        background: "#e9ecef",
                        cursor: "pointer",
                        borderRadius: 2,
                      }}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => setShowAddId(true)}
                      style={{
                        fontSize: 11.5,
                        padding: "3px 10px",
                        border: "1px solid #adb5bd",
                        background: "#e9ecef",
                        cursor: "pointer",
                        borderRadius: 2,
                      }}
                    >
                      Edit
                    </button>
                    <button
                      style={{
                        fontSize: 11.5,
                        padding: "3px 10px",
                        border: "1px solid #adb5bd",
                        background: "#e9ecef",
                        cursor: "pointer",
                        borderRadius: 2,
                      }}
                    >
                      Delete
                    </button>
                  </div>

                  {/* SSN display */}
                  {customer.ssn && (
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#495057",
                          marginBottom: 4,
                        }}
                      >
                        Social Security Number:
                      </div>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 12,
                          border: "1px solid #adb5bd",
                          marginBottom: 6,
                        }}
                      >
                        <thead>
                          <tr style={{ background: "#e9ecef" }}>
                            {["Type", "ID Number", ""].map((h) => (
                              <th
                                key={h}
                                style={{
                                  padding: "3px 6px",
                                  border: "1px solid #adb5bd",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  fontSize: 11.5,
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ background: "white" }}>
                            <td
                              style={{
                                padding: "3px 6px",
                                border: "1px solid #dee2e6",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: 4,
                                  alignItems: "center",
                                }}
                              >
                                <span style={{ fontSize: 13 }}>🔒</span>
                                <span>Social Security #</span>
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "3px 6px",
                                border: "1px solid #dee2e6",
                                fontFamily: "monospace",
                                fontWeight: 700,
                                color: "#6c757d",
                              }}
                            >
                              ***-**-
                              {(customer.ssn || "")
                                .replace(/\D/g, "")
                                .slice(-4)
                                .padStart(4, "*")}
                              <span
                                style={{
                                  fontSize: 10,
                                  color: "#adb5bd",
                                  marginLeft: 6,
                                  fontFamily: "sans-serif",
                                  fontWeight: 400,
                                }}
                              >
                                🔒
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "3px 6px",
                                border: "1px solid #dee2e6",
                                fontSize: 11,
                              }}
                            ></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#495057",
                      marginBottom: 4,
                    }}
                  >
                    Contact:
                  </div>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                      border: "1px solid #adb5bd",
                      marginBottom: 6,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#e9ecef" }}>
                        {["Type", "Number"].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "3px 6px",
                              border: "1px solid #adb5bd",
                              textAlign: "left",
                              fontWeight: 600,
                              fontSize: 11.5,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customer.contacts?.map((ct, i) => (
                        <tr
                          key={i}
                          style={{
                            background: i % 2 === 0 ? "white" : "#f8f9fa",
                          }}
                        >
                          <td
                            style={{
                              padding: "3px 6px",
                              border: "1px solid #dee2e6",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: 4,
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontSize: 13 }}>📞</span>
                              <span>{ct.type}</span>
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "3px 6px",
                              border: "1px solid #dee2e6",
                              fontWeight: 600,
                            }}
                          >
                            {ct.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      onClick={() => setShowAddContact(true)}
                      style={{
                        fontSize: 11.5,
                        padding: "3px 10px",
                        border: "1px solid #adb5bd",
                        background: "#e9ecef",
                        cursor: "pointer",
                        borderRadius: 2,
                      }}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => setShowAddContact(true)}
                      style={{
                        fontSize: 11.5,
                        padding: "3px 10px",
                        border: "1px solid #adb5bd",
                        background: "#e9ecef",
                        cursor: "pointer",
                        borderRadius: 2,
                      }}
                    >
                      Edit
                    </button>
                    <button
                      style={{
                        fontSize: 11.5,
                        padding: "3px 10px",
                        border: "1px solid #adb5bd",
                        background: "#e9ecef",
                        cursor: "pointer",
                        borderRadius: 2,
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* DETAILS TAB */}
            {tab === "details" && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#495057",
                      marginBottom: 8,
                      paddingBottom: 4,
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    Personal Details
                  </div>
                  {[
                    ["Date of Birth", "dateOfBirth", "date"],
                    ["Sex", "sex", "text"],
                    ["Height", "height", "text"],
                    ["Weight", "weight", "text"],
                    ["Eye Color", "eyeColor", "text"],
                    ["Hair Color", "hairColor", "text"],
                  ].map(([l, k, t]) => (
                    <LblInput
                      key={k}
                      label={l}
                      value={
                        c[k]
                          ? k === "dateOfBirth"
                            ? new Date(c[k] + "T12:00:00").toLocaleDateString("en-US", {
                                timeZone: "America/Chicago",
                                month: "2-digit",
                                day: "2-digit",
                                year: "numeric",
                              })
                            : c[k]
                          : ""
                      }
                      readOnly={!editMode}
                      onChange={(e) => setF(k, e.target.value)}
                    />
                  ))}
                  {customer.ssn && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        marginTop: 6,
                        padding: "5px 8px",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                      }}
                    >
                      <span style={{ color: "#6c757d", minWidth: 80 }}>
                        SSN:
                      </span>
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          color: "#6c757d",
                          letterSpacing: 1,
                        }}
                      >
                        ***-**-
                        {(customer.ssn || "")
                          .replace(/\D/g, "")
                          .slice(-4)
                          .padStart(4, "*")}
                      </span>
                      <span style={{ fontSize: 13, marginLeft: 2 }}>🔒</span>
                    </div>
                  )}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#495057",
                      marginBottom: 8,
                      paddingBottom: 4,
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    ID Details
                  </div>
                  {customer.identifications?.map((idDoc, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: 10,
                        padding: 8,
                        background: "#f8f9fa",
                        borderRadius: 4,
                        border: "1px solid #dee2e6",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 12,
                          color: "#0d6efd",
                          marginBottom: 4,
                        }}
                      >
                        ID #{i + 1} — {idDoc.type}
                      </div>
                      {[
                        ["Number", idDoc.idNumber],
                        ["State", idDoc.issuedState],
                        ["Class", idDoc.issuedClass],
                        ["Expiry", fmtD(idDoc.expiryDate)],
                        ["Restrictions", idDoc.restrictions || "NONE"],
                        ["Endorsements", idDoc.endorsements || "NONE"],
                      ].map(([l, v]) => (
                        <div
                          key={l}
                          style={{
                            display: "flex",
                            gap: 6,
                            fontSize: 12,
                            marginBottom: 2,
                          }}
                        >
                          <span style={{ color: "#6c757d", minWidth: 80 }}>
                            {l}:
                          </span>
                          <span
                            style={{
                              fontFamily:
                                l === "Number" ? "monospace" : "inherit",
                              fontWeight: l === "Number" ? 700 : 400,
                              color:
                                l === "Expiry" &&
                                idDoc.expiryDate &&
                                new Date(idDoc.expiryDate) < new Date()
                                  ? "#dc2626"
                                  : "#212529",
                            }}
                          >
                            {v || "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* EXTENDED INFO TAB */}
            {tab === "extended" && (
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    marginBottom: 10,
                    color: "#495057",
                  }}
                >
                  Employer / Work Information
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  {[
                    ["Employer Name", "employer.name"],
                    ["Employer Phone", "employer.phone"],
                    ["Employer Address", "employer.address"],
                  ].map(([l, k]) => {
                    const [p, ch] = k.split(".");
                    const val = ch ? c[p]?.[ch] : c[k];
                    return (
                      <LblInput
                        key={k}
                        label={l}
                        value={val || ""}
                        readOnly={!editMode}
                        onChange={(e) =>
                          ch
                            ? setForm((f) => ({
                                ...f,
                                [p]: { ...f[p], [ch]: e.target.value },
                              }))
                            : setF(k, e.target.value)
                        }
                      />
                    );
                  })}
                </div>
                <div style={{ marginTop: 14 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      marginBottom: 8,
                      color: "#495057",
                    }}
                  >
                    Notes
                  </div>
                  <textarea
                    readOnly={!editMode}
                    value={c.notes || ""}
                    onChange={(e) => setF("notes", e.target.value)}
                    style={{
                      width: "100%",
                      padding: 8,
                      border: "1px solid #adb5bd",
                      borderRadius: 4,
                      fontSize: 13,
                      resize: "vertical",
                      background: !editMode ? "#f8f9fa" : "white",
                    }}
                    rows={4}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
            )}

            {/* MISC TAB */}
            {tab === "misc" && (
              <div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#495057",
                        marginBottom: 8,
                      }}
                    >
                      Account Status
                    </div>
                    {[
                      ["Account ID", customer._id?.slice(-8).toUpperCase()],
                      ["Status", customer.isActive ? "Active" : "Inactive"],
                      ["Created", fmtD(customer.createdAt)],
                      ["Yearly Total", fmt(yearlyTotal)],
                      ["Total Checks", cheques.length],
                      ["Agent", customer.createdBy?.name || "—"],
                    ].map(([l, v]) => (
                      <div
                        key={l}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "5px 0",
                          borderBottom: "1px solid #f0f0f0",
                          fontSize: 12.5,
                        }}
                      >
                        <span style={{ color: "#6c757d" }}>{l}</span>
                        <strong>{v}</strong>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#495057",
                        marginBottom: 8,
                      }}
                    >
                      Fingerprint
                    </div>
                    <div
                      style={{
                        padding: "8px",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                        fontSize: 12.5,
                      }}
                    >
                      <div style={{ marginBottom: 4 }}>
                        Status:{" "}
                        <strong>
                          {customer.fingerprint?.enrolled
                            ? "✅ Enrolled"
                            : "❌ Not Enrolled"}
                        </strong>
                      </div>
                      {customer.fingerprint?.enrolledAt && (
                        <div>
                          Enrolled: {fmtD(customer.fingerprint.enrolledAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── CHECK CASHING HISTORY TABLE ── */}
          <div style={{ borderTop: "2px solid #adb5bd", background: "white" }}>
            {/* Sub-tabs */}
            <div
              style={{
                display: "flex",
                padding: "4px 8px 0",
                background: "#f0f0f0",
                borderBottom: "1px solid #adb5bd",
              }}
            >
              <TabBtn
                label="Check Cashing"
                active={subTab === "cashing"}
                onClick={() => setSubTab("cashing")}
              />
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  paddingBottom: 4,
                }}
              >
                <button
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    border: "1px solid #adb5bd",
                    background: "#e9ecef",
                    cursor: "pointer",
                    borderRadius: 2,
                  }}
                >
                  Check Pictures
                </button>
                <button
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    border: "1px solid #adb5bd",
                    background: "#e9ecef",
                    cursor: "pointer",
                    borderRadius: 2,
                  }}
                >
                  Check Search
                </button>
              </div>
            </div>

            {/* ── Check Cashing sub-tab ── */}
            {subTab === "cashing" && (
              <>
                {/* Table */}
                <div style={{ maxHeight: 180, overflowY: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr style={{ background: "#343a40", color: "white" }}>
                        {[
                          "Date Cashed",
                          "Check #",
                          "Amount",
                          "Fee",
                          "Pay Out",
                          "VC",
                          "Maker",
                          "Status",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "4px 8px",
                              textAlign: "left",
                              fontWeight: 600,
                              fontSize: 11.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cheques.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            style={{
                              textAlign: "center",
                              padding: 20,
                              color: "#6c757d",
                              fontSize: 12,
                            }}
                          >
                            No check cashing history
                          </td>
                        </tr>
                      ) : (
                        cheques.map((ch, i) => (
                          <tr
                            key={ch._id}
                            style={{
                              background: i % 2 === 0 ? "white" : "#f8f9fa",
                              cursor:
                                ch.checkImages?.front || ch.checkImages?.back
                                  ? "pointer"
                                  : "default",
                            }}
                            title={
                              ch.checkImages?.front || ch.checkImages?.back
                                ? "Click to view check image"
                                : ""
                            }
                            onClick={() => {
                              if (
                                ch.checkImages?.front ||
                                ch.checkImages?.back
                              ) {
                                setChequeModal({
                                  ch,
                                  side: ch.checkImages?.front
                                    ? "front"
                                    : "back",
                                });
                              }
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#e8f4fd")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background =
                                i % 2 === 0 ? "white" : "#f8f9fa")
                            }
                          >
                            <td
                              style={{
                                padding: "3px 8px",
                                borderBottom: "1px solid #dee2e6",
                              }}
                            >
                              {fmtD(ch.dateCashed)}
                            </td>
                            <td
                              style={{
                                padding: "3px 8px",
                                borderBottom: "1px solid #dee2e6",
                                fontFamily: "monospace",
                                fontWeight: 700,
                              }}
                            >
                              {ch.checkNumber}
                            </td>
                            <td
                              style={{
                                padding: "3px 8px",
                                borderBottom: "1px solid #dee2e6",
                                fontWeight: 700,
                              }}
                            >
                              {fmt(ch.amount)}
                            </td>
                            <td
                              style={{
                                padding: "3px 8px",
                                borderBottom: "1px solid #dee2e6",
                                color: "#dc2626",
                              }}
                            >
                              {fmt(ch.fee)}
                            </td>
                            <td
                              style={{
                                padding: "3px 8px",
                                borderBottom: "1px solid #dee2e6",
                                color: "#16a34a",
                                fontWeight: 700,
                              }}
                            >
                              {fmt(ch.payOut)}
                            </td>
                            <td
                              style={{
                                padding: "3px 8px",
                                borderBottom: "1px solid #dee2e6",
                                fontSize: 11,
                              }}
                            >
                              {ch.verifyCode?.grade
                                ? `G${ch.verifyCode.grade}`
                                : "—"}
                            </td>
                            <td
                              style={{
                                padding: "3px 8px",
                                borderBottom: "1px solid #dee2e6",
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {ch.maker || "—"}
                            </td>
                            <td
                              style={{
                                padding: "3px 8px",
                                borderBottom: "1px solid #dee2e6",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                }}
                              >
                                <span
                                  style={{
                                    background: STATUS_COLOR[ch.status] + "22",
                                    color: STATUS_COLOR[ch.status],
                                    padding: "1px 6px",
                                    borderRadius: 10,
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                >
                                  {ch.status}
                                </span>
                                {(ch.checkImages?.front ||
                                  ch.checkImages?.back) && (
                                  <span
                                    title="Has check image — click row to view"
                                    style={{ fontSize: 12, cursor: "pointer" }}
                                  >
                                    🖼️
                                  </span>
                                )}
                                <button
                                  title="View / Modify this check"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/cheques/${ch._id}`);
                                  }}
                                  style={{
                                    background: "none",
                                    border: "1px solid #dee2e6",
                                    borderRadius: 5,
                                    cursor: "pointer",
                                    padding: "1px 6px",
                                    fontSize: 11,
                                    color: "#6c757d",
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor =
                                      "#1a7a4a";
                                    e.currentTarget.style.color = "#1a7a4a";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor =
                                      "#dee2e6";
                                    e.currentTarget.style.color = "#6c757d";
                                  }}
                                >
                                  ✏️ Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Table footer */}
                {cheques.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 20,
                      padding: "5px 10px",
                      background: "#e9ecef",
                      borderTop: "1px solid #adb5bd",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    <span>Total Records: {cheques.length}</span>
                    <span>
                      Total Cashed:{" "}
                      <span style={{ color: "#1a7a4a" }}>
                        {fmt(cheques.reduce((s, x) => s + x.amount, 0))}
                      </span>
                    </span>
                    <span>
                      Total Fees:{" "}
                      <span style={{ color: "#dc2626" }}>
                        {fmt(cheques.reduce((s, x) => s + x.fee, 0))}
                      </span>
                    </span>
                    <span>
                      Net Payout:{" "}
                      <span style={{ color: "#16a34a" }}>
                        {fmt(cheques.reduce((s, x) => s + (x.payOut || 0), 0))}
                      </span>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── CHEQUE IMAGE LIGHTBOX MODAL ── */}
        {chequeModal && (
          <div
            onClick={() => setChequeModal(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "rgba(0,0,0,0.82)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "white",
                borderRadius: 10,
                padding: "18px 20px",
                maxWidth: "92vw",
                maxHeight: "92vh",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
              }}
            >
              {/* Modal header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    Check #{chequeModal.ch.checkNumber} —{" "}
                    <span
                      style={{
                        textTransform: "uppercase",
                        color:
                          chequeModal.side === "front" ? "#1a7a4a" : "#495057",
                      }}
                    >
                      {chequeModal.side}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#6c757d" }}>
                    {fmtD(chequeModal.ch.dateCashed)} ·{" "}
                    {fmt(chequeModal.ch.amount)} · {chequeModal.ch.maker || ""}
                  </div>
                </div>
                {/* Front / Back toggle */}
                <div style={{ display: "flex", gap: 6 }}>
                  {chequeModal.ch.checkImages?.front && (
                    <button
                      onClick={() =>
                        setChequeModal((m) => ({ ...m, side: "front" }))
                      }
                      style={{
                        padding: "4px 12px",
                        fontSize: 12,
                        borderRadius: 4,
                        cursor: "pointer",
                        border: "1px solid #1a7a4a",
                        background:
                          chequeModal.side === "front" ? "#1a7a4a" : "white",
                        color:
                          chequeModal.side === "front" ? "white" : "#1a7a4a",
                        fontWeight: 600,
                      }}
                    >
                      Front
                    </button>
                  )}
                  {chequeModal.ch.checkImages?.back && (
                    <button
                      onClick={() =>
                        setChequeModal((m) => ({ ...m, side: "back" }))
                      }
                      style={{
                        padding: "4px 12px",
                        fontSize: 12,
                        borderRadius: 4,
                        cursor: "pointer",
                        border: "1px solid #6c757d",
                        background:
                          chequeModal.side === "back" ? "#6c757d" : "white",
                        color:
                          chequeModal.side === "back" ? "white" : "#6c757d",
                        fontWeight: 600,
                      }}
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={() => setChequeModal(null)}
                    style={{
                      padding: "4px 10px",
                      fontSize: 14,
                      borderRadius: 4,
                      cursor: "pointer",
                      border: "1px solid #dee2e6",
                      background: "#f8f9fa",
                      color: "#495057",
                      fontWeight: 700,
                      marginLeft: 6,
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Cheque image */}
              <div style={{ flex: 1, overflow: "auto", textAlign: "center" }}>
                <img
                  src={imgUrl(
                    chequeModal.ch.checkImages?.[chequeModal.side],
                    imgCacheBust[`${chequeModal.ch._id}-${chequeModal.side}`]
                  )}
                  alt={`Check ${chequeModal.side}`}
                  style={{
                    maxWidth: "80vw",
                    maxHeight: "72vh",
                    borderRadius: 6,
                    border: "1px solid #dee2e6",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                    objectFit: "contain",
                  }}
                />
              </div>

              {/* Nav: prev / next cheque */}
              {(() => {
                const list = cheques.filter(
                  (c) => c.checkImages?.front || c.checkImages?.back,
                );
                const idx = list.findIndex((c) => c._id === chequeModal.ch._id);
                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 12,
                    }}
                  >
                    <button
                      disabled={idx <= 0}
                      onClick={() =>
                        setChequeModal({
                          ch: list[idx - 1],
                          side: list[idx - 1].checkImages?.front
                            ? "front"
                            : "back",
                        })
                      }
                      style={{
                        padding: "4px 14px",
                        borderRadius: 4,
                        cursor: idx <= 0 ? "not-allowed" : "pointer",
                        border: "1px solid #dee2e6",
                        background: "#f8f9fa",
                        color: "#495057",
                        opacity: idx <= 0 ? 0.4 : 1,
                      }}
                    >
                      ← Prev
                    </button>
                    <span style={{ color: "#6c757d" }}>
                      {idx + 1} of {list.length}
                    </span>
                    <button
                      disabled={idx >= list.length - 1}
                      onClick={() =>
                        setChequeModal({
                          ch: list[idx + 1],
                          side: list[idx + 1].checkImages?.front
                            ? "front"
                            : "back",
                        })
                      }
                      style={{
                        padding: "4px 14px",
                        borderRadius: 4,
                        cursor:
                          idx >= list.length - 1 ? "not-allowed" : "pointer",
                        border: "1px solid #dee2e6",
                        background: "#f8f9fa",
                        color: "#495057",
                        opacity: idx >= list.length - 1 ? 0.4 : 1,
                      }}
                    >
                      Next →
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── RIGHT: Action Buttons ── */}
        <div
          className="profile-right-col"
          style={{
            width: 130,
            background: "#f8f9fa",
            borderLeft: "1px solid #adb5bd",
            padding: 8,
            flexShrink: 0,
          }}
        >
          <ActionBtn
            label="Cash Check"
            color="#1a7a4a"
            onClick={() => setShowCash(true)}
          />
          <div style={{ height: 1, background: "#dee2e6", margin: "5px 0" }} />
          <ActionBtn
            label="Modify Check"
            color="#6c757d"
            onClick={() => {
              if (cheques.length > 0) {
                navigate(`/cheques/${cheques[0]._id}`);
              } else {
                navigate(`/cheques?customer=${id}`);
              }
            }}
          />
          <div style={{ height: 1, background: "#dee2e6", margin: "5px 0" }} />

          <ActionBtn
            label="Print Profile"
            color="#6c757d"
            onClick={() => window.print()}
          />

          <div style={{ height: 1, background: "#dee2e6", margin: "5px 0" }} />
          <ActionBtn
            label="Close"
            color="#6c757d"
            onClick={() => navigate("/customers")}
          />

          {isIdExpired(customer) && (
            <>
              <div
                style={{ height: 1, background: "#dee2e6", margin: "5px 0" }}
              />
              <ActionBtn
                label="⚠️ Renew Expired ID"
                color="#dc2626"
                disabled={scanningId}
                onClick={() => idFrontRef.current?.click()}
              />
            </>
          )}
        </div>
      </div>

      {/* ── CHEQUE IMAGES — full width below main profile box ── */}
      {cheques.filter((ch) => ch.checkImages?.front || ch.checkImages?.back)
        .length > 0 && (
        <div
          style={{
            marginTop: 10,
            border: "2px solid #adb5bd",
            borderRadius: 6,
            background: "white",
            overflow: "hidden",
            fontFamily: "Tahoma, Arial, sans-serif",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#e9ecef",
              borderBottom: "2px solid #adb5bd",
              padding: "6px 12px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#212529",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              🖼️ Cheque Images
            </span>
            <span style={{ fontSize: 11, color: "#6c757d", marginLeft: 4 }}>
              {
                cheques.filter(
                  (ch) => ch.checkImages?.front || ch.checkImages?.back,
                ).length
              }{" "}
              cheques with images — newest first
            </span>
          </div>
          {/* Cards row — full width horizontal scroll */}
          <div
            style={{
              display: "flex",
              gap: 12,
              padding: "12px 14px",
              overflowX: "auto",
            }}
          >
            {cheques
              .filter((ch) => ch.checkImages?.front || ch.checkImages?.back)
              .sort((a, b) => new Date(b.dateCashed) - new Date(a.dateCashed))
              .map((ch) => (
                <div
                  key={ch._id}
                  style={{
                    flexShrink: 0,
                    width: 200,
                    border: "1px solid #dee2e6",
                    borderRadius: 6,
                    background: "#fafafa",
                    overflow: "hidden",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                  }}
                >
                  {/* Card header */}
                  <div
                    style={{
                      padding: "5px 10px",
                      background: "#f0f0f0",
                      borderBottom: "1px solid #dee2e6",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontFamily: "monospace",
                        fontSize: 12,
                      }}
                    >
                      #{ch.checkNumber}
                    </span>
                    <span
                      style={{
                        color: "#1a7a4a",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      {fmt(ch.amount)}
                    </span>
                  </div>
                  <div
                    style={{
                      padding: "4px 10px 6px",
                      fontSize: 11,
                      color: "#6c757d",
                    }}
                  >
                    {fmtD(ch.dateCashed)}
                  </div>
                  {/* Thumbnails */}
                  <div
                    style={{ display: "flex", gap: 6, padding: "0 10px 10px" }}
                  >
                    {ch.checkImages?.front && (
                      <div
                        onClick={() => setChequeModal({ ch, side: "front" })}
                        style={{
                          padding: 0,
                          border: "2px solid #1a7a4a",
                          borderRadius: 5,
                          cursor: "pointer",
                          background: "none",
                          overflow: "hidden",
                          flex: 1,
                          height: 80,
                          position: "relative",
                          transition: "transform 0.12s, box-shadow 0.12s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.04)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0,0,0,0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                        title="Click to enlarge front"
                      >
                        <img
                          src={
                            imgUrl(ch.checkImages.front, imgCacheBust[`${ch._id}-front`])
                          }
                          alt="Front"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRecropCheque(ch._id, "front");
                          }}
                          disabled={recroppingKey === `${ch._id}-front`}
                          title="Re-crop this image"
                          style={{
                            position: "absolute",
                            top: 3,
                            right: 3,
                            width: 20,
                            height: 20,
                            padding: 0,
                            border: "none",
                            borderRadius: 4,
                            background: "rgba(0,0,0,0.55)",
                            color: "white",
                            fontSize: 11,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor:
                              recroppingKey === `${ch._id}-front`
                                ? "default"
                                : "pointer",
                          }}
                        >
                          {recroppingKey === `${ch._id}-front` ? (
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                border: "2px solid white",
                                borderTopColor: "transparent",
                                borderRadius: "50%",
                                animation: "spin 0.8s linear infinite",
                              }}
                            />
                          ) : (
                            "✂️"
                          )}
                        </button>
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "rgba(26,122,74,0.88)",
                            color: "white",
                            fontSize: 9,
                            textAlign: "center",
                            padding: "2px 0",
                            fontWeight: 700,
                          }}
                        >
                          FRONT
                        </div>
                      </div>
                    )}
                    {ch.checkImages?.back && (
                      <div
                        onClick={() => setChequeModal({ ch, side: "back" })}
                        style={{
                          padding: 0,
                          border: "2px solid #6c757d",
                          borderRadius: 5,
                          cursor: "pointer",
                          background: "none",
                          overflow: "hidden",
                          flex: 1,
                          height: 80,
                          position: "relative",
                          transition: "transform 0.12s, box-shadow 0.12s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.04)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0,0,0,0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                        title="Click to enlarge back"
                      >
                        <img
                          src={
                            imgUrl(ch.checkImages.back, imgCacheBust[`${ch._id}-back`])
                          }
                          alt="Back"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRecropCheque(ch._id, "back");
                          }}
                          disabled={recroppingKey === `${ch._id}-back`}
                          title="Re-crop this image"
                          style={{
                            position: "absolute",
                            top: 3,
                            right: 3,
                            width: 20,
                            height: 20,
                            padding: 0,
                            border: "none",
                            borderRadius: 4,
                            background: "rgba(0,0,0,0.55)",
                            color: "white",
                            fontSize: 11,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor:
                              recroppingKey === `${ch._id}-back`
                                ? "default"
                                : "pointer",
                          }}
                        >
                          {recroppingKey === `${ch._id}-back` ? (
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                border: "2px solid white",
                                borderTopColor: "transparent",
                                borderRadius: "50%",
                                animation: "spin 0.8s linear infinite",
                              }}
                            />
                          ) : (
                            "✂️"
                          )}
                        </button>
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "rgba(108,117,125,0.88)",
                            color: "white",
                            fontSize: 9,
                            textAlign: "center",
                            padding: "2px 0",
                            fontWeight: 700,
                          }}
                        >
                          BACK
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Fee / payout */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "0 10px 8px",
                      fontSize: 11,
                    }}
                  >
                    <span>
                      Fee: <b style={{ color: "#dc2626" }}>{fmt(ch.fee)}</b>
                    </span>
                    <span>
                      Out: <b style={{ color: "#1a7a4a" }}>{fmt(ch.payOut)}</b>
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ── ID LIGHTBOX ── */}
      {idLightbox && (
        <div
          onClick={() => setIdLightbox(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(0,0,0,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            cursor: "zoom-out",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "95vw",
              maxHeight: "92vh",
            }}
          >
            <img
              src={idLightbox}
              alt="ID enlarged"
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                borderRadius: 10,
                boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
                objectFit: "contain",
                display: "block",
              }}
            />
            <button
              onClick={() => setIdLightbox(null)}
              style={{
                position: "absolute",
                top: -14,
                right: -14,
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "white",
                border: "none",
                cursor: "pointer",
                fontSize: 16,
                fontWeight: 700,
                color: "#333",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
            >
              ✕
            </button>
            <div
              style={{
                textAlign: "center",
                marginTop: 10,
                fontSize: 12,
                color: "rgba(255,255,255,0.6)",
              }}
            >
              Click anywhere outside or ✕ to close
            </div>
          </div>
        </div>
      )}

      {/* ── PROFILE PHOTO CAMERA ── */}
      {showProfileCamera && (
        <DocumentCameraModal
          open={showProfileCamera}
          title="Take Profile Photo"
          label="profile photo"
          aspectRatio={1}
          onClose={() => setShowProfileCamera(false)}
          onCapture={handleProfilePhotoFromCamera}
        />
      )}

      {/* ── ADD ID MODAL ── */}
      {showAddId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 6,
              width: 420,
              boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
              fontFamily: "sans-serif",
            }}
          >
            <div
              style={{
                background: "#1a7a4a",
                color: "white",
                padding: "8px 14px",
                borderRadius: "6px 6px 0 0",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontWeight: 700 }}>Add Identification</span>
              <button
                onClick={() => setShowAddId(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 16 }}>
              {[
                ["ID Type", "type", "select"],
                ["ID Number", "idNumber", "text"],
                ["Issued State", "issuedState", "select-state"],
                ["Class", "issuedClass", "text"],
                ["Expiry Date", "expiryDate", "date"],
              ].map(([l, k, t]) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <div
                    style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}
                  >
                    {l}
                  </div>
                  {t === "select" ? (
                    <select
                      value={newId[k]}
                      onChange={(e) =>
                        setNewId((n) => ({ ...n, [k]: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: "5px 8px",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        fontSize: 13,
                      }}
                    >
                      {[
                        "Driver License",
                        "Social Security #",
                        "Passport",
                        "State ID",
                        "Military ID",
                        "Other",
                      ].map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  ) : t === "select-state" ? (
                    <select
                      value={newId[k]}
                      onChange={(e) =>
                        setNewId((n) => ({ ...n, [k]: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: "5px 8px",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        fontSize: 13,
                      }}
                    >
                      {US_STATES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={t}
                      value={newId[k]}
                      onChange={(e) =>
                        setNewId((n) => ({ ...n, [k]: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: "5px 8px",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        fontSize: 13,
                      }}
                    />
                  )}
                </div>
              ))}
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
              >
                <button
                  onClick={() => setShowAddId(false)}
                  style={{
                    padding: "6px 16px",
                    border: "1px solid #ccc",
                    background: "white",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddId}
                  style={{
                    padding: "6px 18px",
                    background: "#1a7a4a",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  Add ID
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD CONTACT MODAL ── */}
      {showAddContact && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 6,
              width: 360,
              boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
              fontFamily: "sans-serif",
            }}
          >
            <div
              style={{
                background: "#1a7a4a",
                color: "white",
                padding: "8px 14px",
                borderRadius: "6px 6px 0 0",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontWeight: 700 }}>Add Contact</span>
              <button
                onClick={() => setShowAddContact(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 16 }}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>
                  Type
                </div>
                <select
                  value={newContact.type}
                  onChange={(e) =>
                    setNewContact((c) => ({ ...c, type: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "5px 8px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                >
                  {[
                    "Home Phone",
                    "Cell Phone",
                    "Work Phone",
                    "Email",
                    "Other",
                  ].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>
                  Number / Value
                </div>
                <input
                  value={newContact.value}
                  onChange={(e) =>
                    setNewContact((c) => ({ ...c, value: e.target.value }))
                  }
                  placeholder="(248) 548-3020"
                  style={{
                    width: "100%",
                    padding: "5px 8px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                />
              </div>
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
              >
                <button
                  onClick={() => setShowAddContact(false)}
                  style={{
                    padding: "6px 16px",
                    border: "1px solid #ccc",
                    background: "white",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddContact}
                  style={{
                    padding: "6px 18px",
                    background: "#1a7a4a",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  Add Contact
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CASH CHECK MODAL ── */}
      {showCash && (
        <CashCheckModal
          customer={customer}
          onClose={() => setShowCash(false)}
          onSuccess={() => {
            setShowCash(false);
            load();
          }}
        />
      )}
    </Layout>
  );
}
